/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: Dashboard
 * (C) Copyright IBM Corp. 2020, 2022
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'@waca/dashboard-common/js/utils/ContentUtil',
	'@waca/dashboard-common/js/core/APIFactory',
	'./DashboardPropertiesV2API',
	'mobx'
], function (ContentUtil, APIFactory, DashboardPropertiesV2API, Mobx) {

	const SELECTION_CONTEXT = {
		id: 'parentPanel'
	};

	class DashboardPropertiesV2 {

		constructor({ features }) {
			this._api = APIFactory.createAPI(this, [DashboardPropertiesV2API]);
			this._features = features;
			this._eventRemoveCallback = null;
			this._contextQueue = [
				SELECTION_CONTEXT
			];
			this._features['Canvas'].on('change:selections:select', this._onChangeSelectionsHandler, this);
			this._features['Canvas'].on('change:selections:deselect', this._onChangeSelectionsHandler, this);
		}

		destroy() {
			this.deactivate();
			if (this._features['Canvas']) {
				this._features['Canvas'].off('change:selections:select', this._onChangeSelectionsHandler, this);
				this._features['Canvas'].off('change:selections:deselect', this._onChangeSelectionsHandler, this);
			}
			this._api = null;
			this._features = null;
			this._contextQueue = null;
		}

		getAPI() {
			return this._api;
		}

		_isActivated() {
			return !!this._context;
		}

		_createContextDefinition(context) {
			const propertyList = this._extendPropertyList(context.propertyList || this._getPropertyList());
			const propertyLayoutList = (context.getPropertyLayoutList ? context.getPropertyLayoutList() : context.propertyLayoutList) || this._getPropertyLayoutList();
			return {
				...context,
				propertyList,
				propertyLayoutList
			};
		}

		activate() {
			const lastContext = this._contextQueue[this._contextQueue.length - 1];
			const ancestors = this._contextQueue.slice(0, this._contextQueue.length - 1);
			this._context = Mobx.observable({
				definition: this._createContextDefinition(lastContext),
				ancestors
			});

			this._handlePropertyChangeHandlers();
		}

		_refreshObservable() {
			const context = this._contextQueue[this._contextQueue.length - 1];
			const ancestors = this._contextQueue.slice(0, this._contextQueue.length - 1); // everything except the last pushed context (which is now current)
			Mobx.runInAction(() => {
				this._context.definition = this._createContextDefinition(context);
				this._context.ancestors = ancestors;
			});
		}

		deactivate() {
			this._context = null; // TODO: destroy mobx observable, is there a better way?
			if (this._eventRemoveCallback) {
				this._eventRemoveCallback();
			}
		}

		_findPropertyById(id) {
			return this._context.definition.propertyList.find(item => item.id === id);
		}

		_onChangeSelectionsHandler() {
			if (this._isActivated()) {
				this._handlePropertyChangeHandlers();
			}
			this.reset();
		}

		_handlePropertyChangeHandlers() {
			if (this._eventRemoveCallback) {
				this._eventRemoveCallback();
			}
			const selectedContents = this._features['Canvas'].getSelectedContentList();
			if (selectedContents.length === 0) {
				const { remove } = this._features['Canvas'].on('change:property', this._onChangeProperty, this);
				this._eventRemoveCallback = remove;
			} else {
				const { remove } = selectedContents[0].on('change:property', this._onChangeProperty, this);
				this._eventRemoveCallback = remove;
			}
		}

		_onChangeProperty(payload) {
			if (!payload.info ||
				!Array.isArray(payload.info.events) ||
				!payload.info.events.length > 0 ||
				!payload.info.events[0].info ||
				!payload.info.events[0].info.name
			) {
				return;
			}

			const info = payload.info.events[0].info;
			const property = this._findPropertyById(info.name);
			// the property may not exist in the property list of the current context because it belongs to the parent context
			// (scenario where user is in Text editing mode and move the widget which triggers the widget position property changed)
			if (property) {
				property.value = info.value;
			}
		}

		getContext() {
			return this._context;
		}

		/**
		 * gets the common property list for single/multiple selections when there is any selection;
		 * if property id is the same for multiple selections, we default to the value from the first selection.
		 * @example propertyList1 = [{id: 'a', value: 'a'}], propertyList2 = [{id: 'a', value: 'b'}]
		 * returns [[{id: 'a', value: 'a'}]]
		 *
		 * gets canvas property list (dashboard content and canvas content) when there is no selection;
		 */
		_getPropertyList() {
			let propertyList;
			const contentList = this._features['Canvas'].getSelectedContentList();
			if (contentList.length) {
				propertyList = [];
				const expandedResults = ContentUtil.getContentAndItsDescendants(contentList);
				const contents = expandedResults.groupCount ? (expandedResults.contents.slice(expandedResults.groupCount) || []) : expandedResults.contents;
				const allContentsPropertyNameList = [];
				for (const content of contents) {
					const contentProperties = content.getPropertyList();
					const propertyIds = contentProperties.map(function (property) {
						return property.id;
					});
					allContentsPropertyNameList.push(propertyIds);
				}
				if (allContentsPropertyNameList.length) {
					const intersectionNameList = allContentsPropertyNameList.reduce((a, b) => a.filter(c => b.includes(c)));
					for (const name of intersectionNameList) {
						const contentPropertyList = contents[0].getPropertyList();
						propertyList.push(contentPropertyList.find(property => property.id === name));
					}
				}
			} else {
				propertyList = this._features['Canvas'].getPropertyList();
			}

			return propertyList;
		}

		_extendPropertyList(propertyList) {
			return propertyList.map((property) => {
				return {
					...property,
					value: property.getPropertyValue(),
					setPropertyValue: (value) => {
						// calling setPropertyValue triggers an event which is handled in the _onChangeProperty
						// event handler, which will update the current mobx property value
						this.setPropertyValue(property.id, value);
					}
				};
			});
		}

		_getPropertyLayoutList() {
			const contentList = this._features['Canvas'].getSelectedContentList();
			if (contentList.length) {
				const expandedResults = ContentUtil.getContentAndItsDescendants(contentList);
				const contents = expandedResults.contents;
				const groupCount = expandedResults.groupCount;
				const tabItems = [];
				let bannerControl;
				for (const content of contents) {
					const contentLayoutProperties = content.getPropertyLayoutList();
					let tabs = contentLayoutProperties;
					if (!bannerControl) {
						const banner = contentLayoutProperties.find(function (layout) { return layout.type === 'Banner'; });
						bannerControl = banner;
					}
					tabs = tabs.filter((layout => layout.type !== 'Banner'));
					tabItems.unshift(tabs);
				}
				return this._mergeCommonLayoutPropertySpecs(bannerControl, groupCount, tabItems);
			} else {
				return this._features['Canvas'].getPropertyLayoutList();
			}
		}

		_mergeCommonLayoutPropertySpecs (bannerControl, groupCount, tabItems) {
			const commonPropertyLayoutList = [];
			// Always have a Banner for V2
			commonPropertyLayoutList.push(bannerControl);
			if (!Array.isArray(tabItems) || tabItems.length < 1) {
				return commonPropertyLayoutList;
			}
			// merge all non-group tab layouts
			while (tabItems.length >  1 + groupCount) {
				tabItems[0] = this._mergePropertySpecs(tabItems[1], tabItems[0], true);
				tabItems.splice(1, 1);
			}

			const commonSharedTabItems = tabItems[0];
			let commonSharedGroupTabItems;
			if (groupCount > 0) {
				tabItems.splice(0, 1);
				// merge all group tab layouts
				while (tabItems.length > 1) {
					tabItems[0] = this._mergePropertySpecs(tabItems[1], tabItems[0], true);
					tabItems.splice(1, 1);
				}
				commonSharedGroupTabItems = tabItems[0];
			}

			// merge all layouts
			if (commonSharedTabItems){
				tabItems[0] = this._mergeGroupPropertySpecs(commonSharedTabItems, commonSharedGroupTabItems);
			}

			if (tabItems[0]) {
				commonPropertyLayoutList.push(...tabItems[0]);
			}
			return commonPropertyLayoutList;
		}

		_mergePropertySpecs(spec1, spec2, onlyKeepCommonProperties) {
			if (!spec1) {
				return spec2;
			} else if (!spec2) {
				return spec1;
			}

			const result = onlyKeepCommonProperties ? [] : spec1;
			spec2.forEach((spec2Item) => {
				// If the property doesn't have an id then simply append it
				if (!spec2Item.id) {
					result.push(spec2Item);
				} else {
					let matchedItem = null;
					for (var i = 0; i < spec1.length; i++) {
						if (spec1[i].id === spec2Item.id) {
							matchedItem = spec1[i];
							break;
						}
					}
					if (matchedItem) {
						if (matchedItem.items && spec2Item.items) {
							// Recursively merge the child item arrays
							matchedItem.items = this._mergePropertySpecs(matchedItem.items, spec2Item.items, onlyKeepCommonProperties);
						}
						if (onlyKeepCommonProperties) {
							result.push(matchedItem);
						}
					} else if (!onlyKeepCommonProperties) {
						result.push(spec2Item);
					}
				}
			});
			return result;
		}

		_mergeGroupPropertySpecs(commonSharedTabItems, commonSharedGroupTabItems){
			if(!commonSharedGroupTabItems){
				return commonSharedTabItems;
			}
			const result = [];
			commonSharedTabItems.forEach((commonSharedTabItem) => {
				let bMatched = false;
				if (commonSharedTabItem.id) {
					for (let i = 0; i < commonSharedGroupTabItems.length; i++) {
						if (commonSharedGroupTabItems[i].id === commonSharedTabItem.id) {
							const matchedItem = commonSharedGroupTabItems[i];
							if (matchedItem.items && commonSharedTabItem.items) {
								matchedItem.items = this._mergeGroupPropertySpecs(commonSharedTabItem.items, matchedItem.items);
							}
							result.push(matchedItem);
							bMatched = true;
							break;
						}
					}
				}
				if (!bMatched) {
					// if name is not specify OR item only exists in children, push the property to the result
					result.push(commonSharedTabItem);
				}
			});

			return result;
		}

		_getTargetContents() {
			let contents;
			if (this._isActivated() && (this._context.definition.contents || []).length > 0) {
				contents = this._context.definition.contents;
			} else {
				const selectedContents = this._features['Canvas'].getSelectedContentList();
				if (selectedContents.length) {
					contents = ContentUtil.getContentAndItsDescendants(selectedContents).contents;
				} else {
					contents = [
						this._features['Canvas']
					];
				}
			}
			return contents;
		}

		setPropertyValue(name, value, transactionToken) {
			let startedTransaction = false;
			if (!transactionToken) {
				transactionToken = this._features['Transaction'].startTransaction();
				startedTransaction = true;
			}
			const contents = this._getTargetContents();
			contents.forEach(content => {
				if (content.getPropertyList().map(property => property.id).includes(name)) {
					content.setPropertyValue(name, value, transactionToken);
				}
			});
			if (startedTransaction) {
				this._features['Transaction'].endTransaction(transactionToken);
			}
		}

		addContext(context) {
			if (!context) {
				throw new Error(`Invalid context provided: ${context}`);
			}
			this._contextQueue.push(context);

			if (this._isActivated()) {
				this._refreshObservable();
			}
		}

		navigateBack() {
			if (this._contextQueue.length === 1) {
				throw new Error('Out of bounds; Unable to navigateBack on the last context');
			}
			this._contextQueue.pop();

			if (this._isActivated()) {
				this._refreshObservable();
			}
		}

		reset(context = SELECTION_CONTEXT) {
			this._contextQueue = [ context ];
			this.refresh();
		}

		refresh() {
			if (this._isActivated()) {
				this._refreshObservable();
			}
		}

		updatePropertySpec(id, key, value) {
			const property = this._findPropertyById(id);
			if (!property) {
				throw new Error(`Invalid property id: ${id}`);
			}
			if (!property.editor || !property.editor.uiControl || !property.editor.uiControl.hasOwnProperty(key)) {
				throw new Error(`Unable to update property spec "${key}" on property ${id}`);
			}
			property.editor.uiControl[key] = value;
		}
	}

	return DashboardPropertiesV2;
});
